/* eslint-disable react/jsx-props-no-spreading */
import fhirpath from 'fhirpath';
import React from 'react';

import { FhirPedidoType } from '@/features/enterprise/models/types';

import { DropdownRef } from '@/components/Dropdown';
import IntegracaoService from '@/features/enterprise/services/IntegracaoService';
import FeatureHighLight from '@/features/features/components/HighLight';
import { supportingInfoFhirPath } from '@/features/fhir/helpers/supportingInfo';
import { MedicamentoConcentracao } from '@/models/Medicamento';
import { FiChevronDown } from 'react-icons/fi';
import FormMedicamento from '../FormMedicamento';
import MenuConcentracoes from '../MenuConcentracoes';
import {
  Actions,
  CollpsableContent,
  ConcentracaoTitle,
  Container,
  CustomDropDown,
  DropdownButton,
  EditButton,
  EditTools,
  EditedLabel,
  ExpandButton,
  Header,
  HeaderSubTitle,
  HeaderTitle,
} from './styles';

interface BoxMedicamentoEditavelProps {
  medicamento: FhirPedidoType;
  disabled?: boolean;
  detailsOpened?: boolean;
  onBeforeEdit?: () => void;
  onAfterEdit?: () => void;
  onAfterEditCancel?: () => void;
}

const BoxMedicamentoEditavel: React.FC<BoxMedicamentoEditavelProps> = ({
  medicamento,
  disabled,
  detailsOpened,
  onBeforeEdit,
  onAfterEdit,
  onAfterEditCancel,
}: BoxMedicamentoEditavelProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const subtitleRef = React.useRef<HTMLHeadingElement>(null);
  const editableContentRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<DropdownRef>(null);

  const [isOpenForEdit, setIsOpenForEdit] = React.useState(
    detailsOpened || false,
  );
  const [isSubtitleExpanded, setIsSubtitleExpanded] = React.useState(false);
  const [
    medicamentoEditavel,
    setMedicamentoEditavel,
  ] = React.useState<FhirPedidoType>(medicamento);

  const [isSubtitleExpandable, setSubtitleExpandable] = React.useState(false);
  const [loadingConfirmation, setloadingConfirmation] = React.useState(false);

  const principioAtivo = React.useMemo(() => {
    return fhirpath.evaluate(
      medicamentoEditavel,
      `
        ${supportingInfoFhirPath}.medicationCodeableConcept.coding.display 
        | ${supportingInfoFhirPath}.medicationCodeableConcept.text
        | MedicationRequest.medicationCodeableConcept.coding.display
        | MedicationRequest.medicationCodeableConcept.text`,
    )[0];
  }, [medicamentoEditavel]);

  const posologia = React.useMemo(() => {
    return fhirpath.evaluate(
      medicamentoEditavel,
      `${supportingInfoFhirPath}.dosageInstruction.text | MedicationRequest.dosageInstruction.text`,
    )[0];
  }, [medicamentoEditavel]);

  const concentracoes = React.useMemo(() => {
    return fhirpath.evaluate(
      medicamento,
      'MedicationRequest.supportingInfo',
    ) as MedicamentoConcentracao[];
  }, [medicamento]);

  const withConcentracoes = React.useMemo(
    () => !!concentracoes.length && medicamento.substitution?.allowedBoolean,
    [concentracoes.length, medicamento.substitution?.allowedBoolean],
  );

  const edited = React.useMemo(() => !!medicamentoEditavel.reportedBoolean, [
    medicamentoEditavel.reportedBoolean,
  ]);

  const canExpandSubtitle = React.useMemo(
    () => posologia && isSubtitleExpandable && !isOpenForEdit,
    [posologia, isOpenForEdit, isSubtitleExpandable],
  );

  const [
    concentracaoSelected,
    onConcentracaoSelected,
  ] = React.useState<MedicamentoConcentracao>(
    concentracoes.find(
      con => con?.extension?.length && con?.extension[0].valueBoolean,
    ) || concentracoes[0],
  );

  const onEditClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      event.stopPropagation();

      setIsOpenForEdit(true);

      if (onBeforeEdit) {
        onBeforeEdit();
      }
    },
    [onBeforeEdit],
  );

  const toggleCollapse = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      // If disabled, collapse should display the edit form
      if (disabled) {
        setIsOpenForEdit(prev => !prev);
      }

      setIsSubtitleExpanded(prev => !prev);
    },
    [disabled],
  );

  const onEditItem = React.useCallback(
    async (item: fhir4.DomainResource) => {
      try {
        setloadingConfirmation(true);
        const result = await IntegracaoService.updateFhirResource(
          item as FhirPedidoType,
        );

        if (result) {
          setIsOpenForEdit(false);
          setIsSubtitleExpanded(false);
          setMedicamentoEditavel(result);
        }
      } finally {
        setloadingConfirmation(false);
        if (onAfterEdit) {
          onAfterEdit();
        }
      }
    },
    [onAfterEdit],
  );

  const onEditCancel = React.useCallback(
    async (item: fhir4.DomainResource) => {
      setMedicamentoEditavel(item as FhirPedidoType);
      setIsOpenForEdit(false);
      setIsSubtitleExpanded(false);
      if (onAfterEditCancel) {
        onAfterEditCancel();
      }
    },
    [onAfterEditCancel],
  );

  const handleTipoSelect = React.useCallback(
    async (concentracao: MedicamentoConcentracao) => {
      if (dropdownRef.current) {
        dropdownRef.current.hide();
      }

      if (medicamentoEditavel.substitution?.allowedBoolean) {
        const id =
          concentracao.reference?.substring(1) ||
          medicamentoEditavel?.supportingInfo?.flatMap(sup =>
            sup.reference?.substring(1),
          )[0] ||
          '';

        try {
          if (onBeforeEdit) {
            onBeforeEdit();
          }

          setloadingConfirmation(true);
          const result = await IntegracaoService.updateVariacaoFhirResource(
            id,
            medicamentoEditavel,
          );

          if (result) {
            setMedicamentoEditavel(result);
            onConcentracaoSelected(concentracao);
          }
        } finally {
          setloadingConfirmation(false);
          if (onAfterEdit) {
            onAfterEdit();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    if (subtitleRef.current) {
      setSubtitleExpandable(
        subtitleRef.current.scrollHeight > subtitleRef.current.clientHeight,
      );
    }
  }, []);

  return (
    <Container
      detailsOpen={isOpenForEdit || isSubtitleExpanded}
      ref={containerRef}
    >
      <Header>
        <HeaderTitle>{principioAtivo}</HeaderTitle>

        <Actions>
          <EditTools>
            {edited && <EditedLabel>editado</EditedLabel>}
            {withConcentracoes && (
              <CustomDropDown
                ref={dropdownRef}
                disabled={disabled}
                menuAlignment="right"
                DropdownToggle={props => (
                  <FeatureHighLight
                    featureName="concentracoes"
                    featureDescription="Agora você pode selecionar a concentração do medicamento aqui."
                  >
                    <DropdownButton
                      {...props}
                      theme="secondary"
                      loading={loadingConfirmation}
                    />
                  </FeatureHighLight>
                )}
                DropdownMenu={
                  <MenuConcentracoes
                    options={concentracoes}
                    onConcentracaoSelect={handleTipoSelect}
                  />
                }
              >
                <ConcentracaoTitle>
                  {concentracaoSelected?.display}
                </ConcentracaoTitle>
                <FiChevronDown size={16} />
              </CustomDropDown>
            )}
            {!disabled && (
              <EditButton onClick={onEditClick} disabled={isOpenForEdit} />
            )}
          </EditTools>

          <ExpandButton
            disabled={!(disabled || canExpandSubtitle)}
            collapsed={isSubtitleExpanded}
            onClick={toggleCollapse}
          />
        </Actions>
      </Header>

      <HeaderSubTitle
        ref={subtitleRef}
        highlight={isSubtitleExpanded || isOpenForEdit}
      >
        {posologia}
      </HeaderSubTitle>

      <CollpsableContent ref={editableContentRef} highlight={isOpenForEdit}>
        <FormMedicamento
          resource={medicamentoEditavel as fhir4.ServiceRequest}
          onSubmit={onEditItem}
          onCancel={onEditCancel}
          withConcentracoes={withConcentracoes}
          loading={loadingConfirmation}
          readOnly={disabled}
        />
      </CollpsableContent>
    </Container>
  );
};

BoxMedicamentoEditavel.defaultProps = {
  disabled: false,
  detailsOpened: false,
};

export default BoxMedicamentoEditavel;
